import {
  Theme,
  base,
  border2,
  rem,
  reset,
  spacing,
  text,
  radius,
  token,
} from '@thiememeulenhoff/lit-sdk';
import * as coreTokens from '@thiememeulenhoff/tokens/core';
import { css, unsafeCSS } from 'lit';
import { tagName } from '../../helpers';

export const tag = tagName('drawer');
const theme = new Theme(tag);

export const styles = [
  base.focus,
  reset.button,
  css`
    /* stylelint-disable declaration-no-important */

    @keyframes slide-in {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(-100%);
      }
    }

    @keyframes slide-out {
      0% {
        display: block;
        transform: translateX(-100%);
      }
      100% {
        display: none;
        transform: translateX(0);
      }
    }

    @keyframes slide-in-left {
      0% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(0);
      }
    }

    @keyframes slide-out-left {
      0% {
        display: block;
        transform: translateX(0);
      }
      100% {
        display: none;
        transform: translateX(-100%);
      }
    }

    @keyframes slide-in-top {
      0% {
        transform: translateY(-100%);
      }
      100% {
        transform: translateY(0);
      }
    }

    @keyframes slide-out-top {
      0% {
        display: block;
        transform: translateY(0);
      }
      100% {
        display: none;
        transform: translateY(-100%);
      }
    }

    @keyframes slide-in-bottom {
      0% {
        transform: translateY(100%);
      }
      100% {
        transform: translateY(0);
      }
    }

    @keyframes slide-out-bottom {
      0% {
        display: block;
        transform: translateY(0);
      }
      100% {
        display: none;
        transform: translateY(100%);
      }
    }

    :where(dialog) {
      block-size: 100vh;
      border: 0;
      box-sizing: border-box;
      inline-size: var(--tm-drawer-size, ${rem(336)});
      inset-block-start: 0;
      inset-inline-start: 100%;
      margin: 0;
      max-block-size: 100svh;
      max-inline-size: 100vw;
      min-inline-size: ${rem(250)};
      ${theme.props([['background', 'background', token('background-secondary')]])};
      opacity: 1;
      padding: 0;
      z-index: 9999;
    }

    :where(dialog.hide) {
      animation: slide-out var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out) !important;
    }

    :host([size='wide']) :where(dialog) {
      inline-size: ${rem(512)};
    }

    :where(dialog[open]) {
      animation: slide-in var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out);
      display: flex;
      flex-direction: column;
      transform: translateX(-100%);
    }

    :host([placement='left']) :where(dialog) {
      inset-inline-start: 0;
      transform: translateX(-100%);
    }

    :host([placement='left']) :where(dialog.hide) {
      animation: slide-out-left var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out) !important;
    }
    :host([placement='left']) :where(dialog[open]) {
      animation: slide-in-left var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out);
      transform: translateX(0);
    }

    :host([placement='top']) :where(dialog) {
      inset-inline-start: 0;
      max-block-size: ${rem(336)};
      min-inline-size: 100dvw;
      transform: translateX(-100%);
    }

    :host([placement='top']) :where(dialog.hide) {
      animation: slide-out-top var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out) !important;
    }

    :host([placement='top']) :where(dialog[open]) {
      animation: slide-in-top var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out);
      transform: translateX(0);
    }

    :host([placement='bottom']) :where(dialog) {
      inset-inline-start: 0;
      margin: auto 0 0;
      max-block-size: ${rem(336)};
      min-inline-size: 100dvw;
      transform: translateX(-100%);
    }

    :host([placement='bottom']) :where(dialog.hide) {
      animation: slide-out-bottom var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out) !important;
    }
    :host([placement='bottom']) :where(dialog[open]) {
      animation: slide-in-bottom var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out);
      transform: translateX(0);
    }

    :where(dialog.hide)::backdrop {
      animation: backdrop-fade-out var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out) forwards !important;
    }

    :where(dialog[open])::backdrop {
      animation: backdrop-fade-in var(--tm-transition-duration-normal)
        var(--tm-transition-function-ease-in-out) forwards;
    }

    @keyframes backdrop-fade-in {
      from {
        background: transparent;
      }
      to {
        background: var(--tm-elevation-backdrop-color);
      }
    }

    @keyframes backdrop-fade-out {
      from {
        background: var(--tm-elevation-backdrop-color);
      }
      to {
        background: transparent;
      }
    }

    @media (width < ${unsafeCSS(coreTokens.BreakpointSmall)}) {
      :where(dialog) {
        inline-size: 100vw;
        min-inline-size: 100vw;
        ${theme.props([
          ['border-radius', 'border-radius-tablet', rem(0, 0, 0, 0)],
        ])};
      }
    }

    :host([show-partly]) :where(dialog) {
      inline-size: 80vw;
      min-inline-size: 80vw;

      ::slotted(*) {
        max-inline-size: unset;
      }
    }

    :host([show-partly][placement='left']) :where(dialog) {
      border-end-end-radius: ${radius('large')};
      border-start-end-radius: ${radius('large')};
      inset-inline-start: 0;
      transform: translateX(0);
    }

    :host([show-partly][placement='right']) :where(dialog) {
      border-end-start-radius: ${radius('large')};
      border-start-start-radius: ${radius('large')};
      inset-inline-start: 100%;
      transform: translateX(-100%);
    }

    :host([show-partly][placement='left']) :where(header),
    :host([show-partly][placement='right']) :where(header) {
      background-color: ${token('background-secondary')};
      border-block-end: ${border2(1, 'drawer-header-border-default')};
    }

    :host([placement='top']) :where(dialog),
    :host([placement='bottom']) :where(dialog) {
      max-block-size: 100%;
    }

    :where(header) {
      align-items: flex-start;
      ${theme.props([
        [
          'background-color',
          'header-background-color',
          token('drawer-header-background-default'),
        ],
        [
          'border-block-end',
          'header-border-block-end',
          border2(1, 'border-subtle'),
        ],
      ])}
      box-sizing: border-box;
      display: flex;
      inset-block-start: 0;
      justify-content: space-between;
      position: sticky;
    }

    :where(span.title) {
      display: block;
      flex: 1;
      font-weight: 600;
      padding: ${spacing(150)};

      ${text('heading', 'medium')};
    }

    :where(content) {
      display: flex;
      flex-direction: column;
      min-block-size: 100%;
      overflow-y: hidden;
    }

    :where(main) {
      flex: 1;
      overflow-y: auto;
      padding: 0;
    }

    :where(footer) {
      border-block-start: ${border2(1, 'border-strong')};
      inset-block-end: 0;
      position: sticky;
    }

    :where(header a-icon-button),
    :where(header .sidebar-icon-container) {
      align-self: center;
      margin-inline: ${spacing(50)};
    }

    :host([show-partly][placement='left'])
      :where(header .sidebar-icon-container) {
      margin-inline-end: ${spacing(300)};
      margin-inline-start: ${spacing(150)};
    }

    .sidebar-icon-container {
      align-items: center;
      block-size: ${rem(14)};
      border: ${border2(2, 'drawer-sidebar-border-default')};
      border-radius: ${radius('small')};
      display: flex;
      inline-size: ${rem(14)};
      justify-content: center;

      &:hover {
        border-color: ${token('drawer-sidebar-border-hover')};
      }
    }

    .sidebar-button {
      background: none;
      border: 0;
      color: ${token('drawer-sidebar-foreground-default')};
      cursor: pointer;
      display: flex;
      padding: 0;

      &:hover {
        color: ${token('drawer-sidebar-foreground-hover')};
      }
    }

    .sidebar-icon {
      font-size: ${rem(12)};
    }

    ::slotted(a-list) {
      block-size: 100%;
      display: block;
    }
  `,
];
